/**
 * filetransfer.ts — SPEC §9.2 file-transfer framing over the `files` data channel.
 *
 * Wire frame: [4-byte big-endian header length][JSON header][raw chunk bytes].
 * Header kinds: offer / accept / chunk / ack / complete / error.
 * Backpressure: pause when bufferedAmount > 8 MiB; resume on
 * bufferedAmountLowThreshold. Integrity via SHA-256 (SPEC §9.2).
 *
 * E2EE (SPEC §10/§11) — WHOLE-FRAME sealing, matching the agent exactly:
 *   the agent (agent/internal/control/fileChanAdapter.SendFrame +
 *   features.FileManager.send) builds the ENTIRE §9.2 frame (header+chunk) and
 *   then E2EE-seals the whole frame as one opaque payload; on receive it opens
 *   the whole payload FIRST and only then deframes (session.go files OnMessage →
 *   maybeOpen → files.HandleFrame → DecodeFileFrame). The header therefore rides
 *   INSIDE the E2EE envelope. The console must do the same so bytes are
 *   wire-identical and the relay sees ciphertext only — sealing just the chunk
 *   (header in cleartext) both leaks metadata AND breaks the agent's deframe.
 */

import type { FileHeader } from '@/types';
import { sha256Hex, toArrayBuffer } from '@/lib/binary';

const BACKPRESSURE_HIGH = 8 * 1024 * 1024; // 8 MiB (SPEC §9.2)
const BACKPRESSURE_LOW = 1 * 1024 * 1024;
const CHUNK_SIZE = 64 * 1024; // 64 KiB chunks (< SCTP 256 KiB cap)

const enc = new TextEncoder();
const dec = new TextDecoder();

/** Seal the whole framed message (header+chunk) into one opaque E2EE payload. */
export type SealFrame = (frame: Uint8Array) => Uint8Array;
/** Open one opaque E2EE payload back into the framed bytes (throws on failure). */
export type OpenFrame = (payload: Uint8Array) => Uint8Array;

/** Encode one framed message: header + optional binary chunk. */
export function frame(header: FileHeader, chunk?: Uint8Array): Uint8Array {
  const headerBytes = enc.encode(JSON.stringify(header));
  const chunkLen = chunk?.length ?? 0;
  const buf = new ArrayBuffer(4 + headerBytes.length + chunkLen);
  const view = new DataView(buf);
  view.setUint32(0, headerBytes.length, false); // big-endian
  const out = new Uint8Array(buf);
  out.set(headerBytes, 4);
  if (chunk) out.set(chunk, 4 + headerBytes.length);
  return out;
}

/**
 * Decode a framed message back into { header, chunk }. Accepts either the raw
 * bytes (post-E2EE-open) or an ArrayBuffer. The chunk slice aliases the input
 * (matches the agent's proto.DecodeFileFrame, which aliases too).
 */
export function deframe(data: Uint8Array | ArrayBuffer): { header: FileHeader; chunk: Uint8Array } {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerLen = view.getUint32(0, false);
  const headerBytes = bytes.subarray(4, 4 + headerLen);
  const header = JSON.parse(dec.decode(headerBytes)) as FileHeader;
  const chunk = bytes.subarray(4 + headerLen);
  return { header, chunk };
}

export type TransferProgress = (sent: number, total: number) => void;

/**
 * Upload a File to the agent over the `files` channel with §9.2 framing and
 * bufferedAmount backpressure. Resolves when the agent sends `complete`.
 *
 * Every outbound frame is sealed AS A WHOLE (header+chunk) via opts.seal before
 * it hits the wire (SPEC §10): the relay only ever sees ciphertext, and the
 * agent opens the whole payload then deframes. Callers MUST pass `seal`/`open`
 * that fail closed when E2EE is not established (never upload in cleartext).
 */
export async function uploadFile(
  ch: RTCDataChannel,
  file: File,
  opts: {
    transferId: string;
    seal: SealFrame;
    open: OpenFrame;
    onProgress?: TransferProgress;
    signal?: AbortSignal;
  },
): Promise<void> {
  ch.bufferedAmountLowThreshold = BACKPRESSURE_LOW;
  const bytes = new Uint8Array(await file.arrayBuffer());
  const sha = await sha256Hex(bytes);

  const send = (header: FileHeader, chunk?: Uint8Array) =>
    ch.send(toArrayBuffer(opts.seal(frame(header, chunk))));

  send({ kind: 'offer', transfer_id: opts.transferId, name: file.name, size: file.size, sha256: sha });

  // Wait for the agent's accept (or error) for this transfer.
  await waitForHeader(ch, opts.open, (h) => {
    if (h.kind === 'accept' && h.transfer_id === opts.transferId) return true;
    if (h.kind === 'error' && h.transfer_id === opts.transferId) throw new Error(h.message);
    return false;
  });

  let offset = 0;
  let seq = 0;
  while (offset < bytes.length) {
    if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const end = Math.min(offset + CHUNK_SIZE, bytes.length);
    // Frame the CLEARTEXT chunk (header+chunk), then seal the whole frame. Sealing
    // the chunk before framing (the old bug) left the header in cleartext AND
    // inflated the chunk so the agent's offset/size bounds check would reject it.
    send({ kind: 'chunk', transfer_id: opts.transferId, seq, offset }, bytes.subarray(offset, end));

    offset = end;
    seq += 1;
    opts.onProgress?.(offset, bytes.length);

    if (ch.bufferedAmount > BACKPRESSURE_HIGH) {
      await waitForDrain(ch);
    }
  }

  send({ kind: 'complete', transfer_id: opts.transferId });
  await waitForHeader(ch, opts.open, (h) => h.kind === 'complete' && h.transfer_id === opts.transferId);
}

/** Receive a download initiated by the agent's `offer`. Returns the assembled blob. */
export interface IncomingTransfer {
  transferId: string;
  name: string;
  size: number;
  sha256: string;
}

/** Pause the send loop until the channel's buffer drains below the low threshold. */
function waitForDrain(ch: RTCDataChannel): Promise<void> {
  return new Promise((resolve) => {
    const onLow = () => {
      ch.removeEventListener('bufferedamountlow', onLow);
      resolve();
    };
    ch.addEventListener('bufferedamountlow', onLow);
  });
}

/**
 * Resolve when an incoming header satisfies `pred`; reject if `pred` throws.
 * Each inbound message is E2EE-opened (whole payload) BEFORE deframing, matching
 * the agent's receive path. Undecryptable / non-frame messages are ignored.
 */
function waitForHeader(
  ch: RTCDataChannel,
  open: OpenFrame,
  pred: (h: FileHeader) => boolean,
): Promise<FileHeader> {
  return new Promise((resolve, reject) => {
    const onMsg = (ev: MessageEvent) => {
      if (!(ev.data instanceof ArrayBuffer)) return;
      let header: FileHeader;
      try {
        ({ header } = deframe(open(new Uint8Array(ev.data))));
      } catch {
        return; /* not a frame for us / undecryptable */
      }
      let ok = false;
      try {
        ok = pred(header);
      } catch (e) {
        ch.removeEventListener('message', onMsg);
        reject(e);
        return;
      }
      if (ok) {
        ch.removeEventListener('message', onMsg);
        resolve(header);
      }
    };
    ch.addEventListener('message', onMsg);
  });
}

export { toArrayBuffer, CHUNK_SIZE, BACKPRESSURE_HIGH };
